import { Mail, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import { useI18n } from '../../i18n';

export default function Footer() {
  const location = useLocation();
  const { t, language } = useI18n();

  const basePath = `/${language}`;

  return (
    <footer className="bg-[#00a63e] px-4 py-12 sm:px-6 md:px-8 lg:px-[120px]">
      <div className="mx-auto max-w-[1440px]">
        {/* Main Content */}
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-16">
          {/* Column 1: Logo & Tagline */}
          <div className="flex flex-col items-center gap-4 md:items-start">
            <Link
              to={basePath}
              className="overflow-hidden rounded-2xl bg-white p-2 transition-opacity hover:opacity-80"
            >
              <img
                src="/images/HospitalLogo.png"
                alt={t.hero.brandName}
                width={1280}
                height={1280}
                loading="lazy"
                className="h-32 w-auto object-contain"
              />
            </Link>
            <p className="text-center text-lg font-medium whitespace-pre-line text-white/95 md:text-start">
              {t.footer.tagline}
            </p>
          </div>

          {/* Column 2: Navigation */}
          <div className="flex flex-col items-center gap-6 md:items-start">
            <h3 className="text-xl font-bold text-white">
              {t.footer.quickLinks}
            </h3>
            <nav className="flex flex-col items-center gap-3 md:items-start">
              {t.nav.items.map((item) => {
                const isActive =
                  !item.isSection &&
                  location.pathname === `${basePath}/${item.id}`;

                if (item.isSection) {
                  const href =
                    location.pathname === basePath ||
                    location.pathname === `${basePath}/`
                      ? `#${item.id}`
                      : `${basePath}#${item.id}`;

                  return (
                    <a
                      key={item.id}
                      href={href}
                      className="text-base font-medium text-white/90 transition-colors hover:text-white hover:underline"
                    >
                      {item.title}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.id}
                    to={`${basePath}/${item.id}`}
                    className={`text-base font-medium transition-colors hover:underline ${
                      isActive
                        ? 'text-white underline'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {item.title}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Column 3: Contact */}
          <div className="flex flex-col items-center gap-6 md:items-start">
            <h3 className="text-xl font-bold text-white">
              {t.footer.contactUs}
            </h3>
            <div className="flex flex-col gap-4">
              {/* Email */}
              <a
                href={`mailto:${t.footer.email}`}
                className="flex items-center gap-3 text-white/90 transition-colors hover:text-white"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <span className="text-base font-medium">{t.footer.email}</span>
              </a>

              {/* Phone */}
              <a
                href={`tel:${t.footer.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-3 text-white/90 transition-colors hover:text-white"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <span dir="ltr" className="text-base font-medium">
                  {t.footer.phone}
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-8 h-px w-full bg-white/20" />

        {/* Bottom Section */}
        <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
          {/* Copyright */}
          <p className="text-center text-sm text-white/80">
            {t.footer.copyright.replace(
              '{year}',
              new Date().getFullYear().toString(),
            )}
          </p>

          {/* Legal Link */}
          <Link
            to="/terms-and-privacy"
            className="text-base font-medium text-white/90 transition-colors hover:text-white hover:underline"
          >
            {t.footer.termsAndPrivacy}
          </Link>
        </div>
      </div>
    </footer>
  );
}
